import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import PasswordModal from '../components/PasswordModal';
import ErrorModal from '../components/ErrorModal';
import { loadPdfFromFile } from './pdfjs';
import getErrorName from './getErrorName';
import { sendEvent } from './analytics';

/**
 * @param {FileState[]} files
 * @param {function} setFiles
 * @param {boolean} [concat]
 * @return {{processing: boolean, handleAccepted: function, errorModal: JSX.Element, fileWithError: PdfFileState}}
 */
const usePdfAccept = (files, setFiles, concat = true) => {
  const { t } = useTranslation(['common', 'convert-and-edit']);
  const [processing, setProcessing] = useState(false);
  const [wrongPasswordError, setWrongPasswordError] = useState(false);
  const [passwordProcessing, setPasswordProcessing] = useState(false);
  const fileWithError = useMemo(() => files.find(({ error }) => error), [files]);

  const handleAccepted = useCallback((acceptedFiles) => {
    setProcessing(true);

    Promise.all(acceptedFiles.map((acceptedFile) => {
      return loadPdfFromFile(acceptedFile)
        .then((pdfDocument) => ({
          key: `${Date.now()}:${Math.random()}`,
          file: acceptedFile,
          pdfDocument,
        }))
        .catch((error) => {
          console.error('loadPdfFromFile error', error);

          return {
            key: `${Date.now()}:${Math.random()}`,
            file: acceptedFile,
            error,
          };
        });
    }))
      .then((newFiles) => {
        setFiles((files) => concat ? files.concat(newFiles) : newFiles);
      })
      .finally(() => {
        setProcessing(false);
      });
  }, [setFiles, concat]);

  const handlePasswordCancel = useCallback(() => {
    sendEvent({
      category: 'password-modal',
      action: 'cancel',
    });

    setFiles((files) => {
      return files.filter((file) => file !== fileWithError);
    });
  }, [fileWithError, setFiles]);

  const handlePasswordSubmit = useCallback(({ password }) => {
    setPasswordProcessing(true);

    loadPdfFromFile(fileWithError.file, password)
      .then((pdfDocument) => {
        sendEvent({
          category: 'password-modal',
          action: 'success',
        });

        setWrongPasswordError(false);

        setFiles((files) => {
          return files.map((file) => {
            if (file === fileWithError) {
              return {
                ...file,
                error: null,
                pdfDocument,
                password,
              };
            }

            return file;
          });
        });
      })
      .catch((error) => {
        console.error('loadPdfFromFile error', error);

        sendEvent({
          category: 'password-modal',
          action: 'error',
        });

        setWrongPasswordError(true);
      })
      .finally(() => {
        setPasswordProcessing(false);
      });
  }, [fileWithError, setFiles]);

  const handleFileErrorModalHide = useCallback(() => {
    setFiles((files) => {
      return files.filter((file) => file !== fileWithError);
    });
  }, [fileWithError, setFiles]);

  let errorModal;

  if (fileWithError?.error) {
    if (getErrorName(fileWithError.error) === 'PasswordException') {
      errorModal = (
        <PasswordModal
          key={[
            files.indexOf(fileWithError),
            fileWithError.file.name,
            fileWithError.file.size,
          ].join(':')}
          filename={fileWithError.file.name}
          error={wrongPasswordError}
          processing={passwordProcessing}
          onCancel={handlePasswordCancel}
          onSubmit={handlePasswordSubmit}
        />
      );
    } else {
      errorModal = (
        <ErrorModal
          title={t('convert-and-edit:read-error-modal.title')}
          text={t('convert-and-edit:read-error-modal.text')}
          show
          onHide={handleFileErrorModalHide}
        />
      );
    }
  }

  return {
    processing,
    fileWithError,
    handleAccepted,
    errorModal,
  };
};

export default usePdfAccept;
